import { useHotkeys } from "react-hotkeys-hook"
import { useNavigate } from "react-router-dom"
import { v4 as uuidv4 } from "uuid"
import { defaultGroupWrapper } from "../data/defaults/objects.types.defaults"
import { createObject } from "tweenly"
import { selectCanRedo, selectCanUndo, selectUndoable } from "../helpers/selector.helpers"
import { getCompanyStatsAsync } from "../store/slices/companyStats.slice"
import {
    clearAndReset,
    prevSaveFile,
    redo,
    setIsTimelineSettingChanged,
    setOpenCanvasSettings,
    setOpenDeleteAlert,
    setOpenDialogCreateNew,
    setOpenDialogToDuplicate,
    setOpenSaveForm,
    setOpenVersionHistoryDetail,
    setSignSaveAs,
    undo,
} from "../store/slices/editor.slice"
import { updateGraphicAsync } from "../store/slices/graphic.slice"
import { AssetRef } from "../store/slices/graphics.slice"
import {
    groupObjectsActionAsync,
    selectObjectById,
    selectObjectsByIds,
    unGroupObjectsAction,
} from "../store/slices/objects.slice"
import { findCommonParentId } from "../store/slices/objects.slice/object/groupObjectsBaseAction"
import { getSimilarGraphicAsync } from "../store/slices/similarGraphics.slice"
import useMultiselectObjectAction from "./editor/useMultiselectObjectAction"
import useWrapSelectedObject from "./editor/useWrapSelectedObject"
import useWrapSelectedObjects from "./editor/useWrapSelectedObjects"
import useHTMLFileContent from "./useHTMLFileContent"
import { useObjectActions } from "./useObjectActions"
import { useAppDispatch, useAppSelector } from "./useRedux"

//[NOTE]: MUST BE used only once in Editor,
//every usage in app invoke action with shortcuts one more time
export const useShortcuts = () => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const canUndo = useAppSelector(selectCanUndo)
    const canRedo = useAppSelector(selectCanRedo)
    const graphic = useAppSelector((state) => state.graphic)
    const assets = useAppSelector((state) => state.assets.data)
    const objects = useAppSelector((state) => selectUndoable(state).objects)
    const selectedObjectsIds = useAppSelector((state) => state.activeObject.selected)
    const { wrapperObject, allObjectsInWrapper } = useWrapSelectedObjects()
    const { wrapperObjectsStyles } = useWrapSelectedObject()
    const commonParentId = findCommonParentId(Object.values(objects.value), selectedObjectsIds)

    const { processFile } = useHTMLFileContent()
    const { moveObjectUp, moveObjectDown } = useObjectActions()
    const { handleDeleteObjects, handleDuplicateObjects } = useMultiselectObjectAction()

    //Undo
    useHotkeys(
        `ctrl+z, meta+z`,
        () => {
            if (canUndo) {
                dispatch(undo())
            }
        },
        [canUndo]
    )
    //Redo
    useHotkeys(
        `ctrl+shift+z, meta+shift+z`,
        () => {
            if (canRedo) {
                dispatch(redo())
            }
        },
        [canRedo]
    )

    //[TO DO]: how to duplicate only selected animation, when select animations automatically select object of animation
    //Duplicate selected objects
    useHotkeys(
        `ctrl+d, meta+d`,
        () => {
            handleDuplicateObjects()
        },
        { preventDefault: true }
    )

    //wrap selected objects
    useHotkeys(
        `ctrl+g, meta+g`,
        () => {
            if (selectedObjectsIds.length === 1) {
                const { width, height, top, left } = wrapperObjectsStyles
                const groupObject = createObject(
                    Object.values(objects.value),
                    defaultGroupWrapper({ width, height, top, left }),
                    commonParentId,
                    "i" + uuidv4(),
                    true
                )
                dispatch(
                    groupObjectsActionAsync(
                        groupObject,
                        selectObjectsByIds(objects, selectedObjectsIds),
                        wrapperObjectsStyles,
                        [wrapperObjectsStyles]
                    )
                )
            } else {
                const { width, height, top, left } = wrapperObject
                const groupObject = createObject(
                    Object.values(objects.value),
                    defaultGroupWrapper({ width, height, top, left }),
                    commonParentId,
                    "i" + uuidv4(),
                    true
                )
                dispatch(
                    groupObjectsActionAsync(
                        groupObject,
                        selectObjectsByIds(objects, selectedObjectsIds),
                        wrapperObject,
                        allObjectsInWrapper
                    )
                )
            }
        },
        { preventDefault: true }
    )
    //unwrap selected objects
    useHotkeys(
        `ctrl+alt+g, meta+alt+g`,
        () => {
            if (selectedObjectsIds.length === 1) {
                const object = selectObjectsByIds(objects, selectedObjectsIds)
                if (object[0].isGroup) {
                    dispatch(unGroupObjectsAction({ object: object[0] }))
                }
            }
        },
        { preventDefault: true }
    )

    //Save graphic
    useHotkeys(
        `ctrl+s, meta+s`,
        () => {
            const file = processFile()
            if (!graphic.data._id) {
                dispatch(prevSaveFile(file))
                dispatch(setOpenSaveForm(true))
            } else {
                let assetsRef: AssetRef[] = assets.map((asset) => ({ id: asset._id }))
                dispatch(prevSaveFile(file))
                dispatch(updateGraphicAsync({ ...graphic.data, assetsRef }, file, false))
                dispatch(setIsTimelineSettingChanged(false))
                dispatch(getCompanyStatsAsync(graphic.data.companyId, true))
            }
        },
        { preventDefault: true }
    )

    //[TO DO]: how to delete only selected animation, when select animations automatically select object of animation
    // Delete selected objects
    useHotkeys(
        `ctrl+del, ctrl+delete, ctrl+backspace,
         meta+del, meta+delete, meta+backspace`,
        () => {
            handleDeleteObjects()
        },
        { preventDefault: true }
    )

    // Move object up
    useHotkeys(
        `ctrl+shift+up, meta+shift+up`,
        () => {
            if (selectedObjectsIds.length === 1) {
                const object = selectObjectById(objects, selectedObjectsIds[0])
                moveObjectUp(object)
            }
        },
        { preventDefault: true },
        // without these the handler kept the selection and objects of its first render
        [objects, selectedObjectsIds]
    )

    // Move object down
    useHotkeys(
        `ctrl+shift+down, meta+shift+down`,
        () => {
            if (selectedObjectsIds.length === 1) {
                const object = selectObjectById(objects, selectedObjectsIds[0])
                moveObjectDown(object)
            }
        },
        { preventDefault: true },
        [objects, selectedObjectsIds]
    )

    // New Graphic
    useHotkeys(
        `ctrl+alt+n, meta+alt+n`,
        () => {
            if (canUndo) {
                dispatch(setOpenDialogCreateNew(true))
            } else {
                dispatch(setOpenSaveForm(false))
                dispatch(setOpenVersionHistoryDetail(false))
                dispatch(clearAndReset(graphic.data._id))
                navigate("/app/editor")
                dispatch(setOpenDialogCreateNew(false))
                dispatch(setOpenCanvasSettings(true))
            }
        },
        { preventDefault: true }
    )

    // Duplicate Graphic
    useHotkeys(
        `ctrl+shift+d, meta+shift+d`,
        () => {
            if (graphic.data._id) {
                if (canUndo) {
                    dispatch(setOpenDialogToDuplicate(true))
                }
                const file = processFile()
                dispatch(prevSaveFile(file))
                dispatch(setOpenSaveForm(true))

                const companyId = graphic.data.companyId
                const regex = "^" + graphic.data.name.replace(/(\()(\d+)(\))$/, () => "")

                dispatch(getSimilarGraphicAsync(companyId, regex))
                dispatch(setOpenVersionHistoryDetail(false))
                dispatch(setSignSaveAs(true))
            }
        },
        { preventDefault: true }
    )

    // Delete Graphic
    useHotkeys(
        `ctrl+shift+del, ctrl+shift+delete, ctrl+shift+backspace,
         meta+shift+del, meta+shift+delete, meta+shift+backspace`,
        () => {
            dispatch(setOpenDeleteAlert(true))
        },
        { preventDefault: true }
    )
}
